import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { tap, catchError, switchMap } from 'rxjs/operators';
import { environment } from './../../../../environments/environment';
import { API } from '../../API';
import { FEDERATION_SLUG } from '../../../../main';

export interface Club {
  club_id: number;
  federation_id: number;
  club_code: string;
  club_name: string;
  Cle_CLUB: string;
  Statut_CLUB: 'ACTI' | 'INAC' | 'DESA';
  club_status?: 'ACTI' | 'INAC' | 'DESA';
  logo_club?: string;
}

export interface Federation {
  federation_id: number;
  federation_acornyme: string;
  federation_name: string;
  logo_url?: string;
}

// TASK-1.07: token is no longer in JSON response — it is set as an HttpOnly cookie
export interface AuthResponse {
  success: boolean;
  user: {
    id: number;
    username: string;
    roles: string[];
    user_object_type: string;
    user_object_id?: number;
    permissions?: Record<string, string[]>;
  };
  club?: Club;
  federation?: Federation;
}

export interface User {
  id: number;
  username: string;
  roles: string[];
  user_object_type: string;
  user_object_id?: number;
  permissions?: Record<string, string[]>;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly baseUrl = environment.apiUrl;
  private userDataSignal = signal<User | null>(null);
  private clubDataSignal = signal<Club | null>(null);
  private federationDataSignal = signal<Federation | null>(null);
  private slugLogoUrlSignal = signal<string | null>(null);
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(private httpClient: HttpClient) {}

  /**
   * Restore session from HttpOnly cookie on app startup.
   * Called by APP_INITIALIZER — if cookie is valid, backend returns user data.
   */
  restoreSession(): Observable<AuthResponse | null> {
    return this.httpClient.get<AuthResponse>(`${this.baseUrl}${API.AUTH.ME}`, { withCredentials: true }).pipe(
      tap(response => {
        if (response.success) {
          this.setUser(response.user);
          if (response.club) this.setClub(response.club);
          if (response.federation) this.setFederation(response.federation);
          this.currentUserSubject.next(response.user);
        }
      }),
      catchError(() => {
        // Cookie expired or invalid — user stays logged out
        return of(null);
      })
    );
  }

  /**
   * TASK 6: Expose federation slug for all components.
   * Extracted once at boot (immutable constant).
   */
  get federationSlug(): string | null {
    return FEDERATION_SLUG;
  }

  private clearOtherPlatformCache(platform: 'club' | 'fed'): void {
    if (platform === 'fed') {
      this.clubDataSignal.set(null);
    } else {
      this.federationDataSignal.set(null);
    }
  }

  login(username: string, password: string, platform: 'club' | 'fed' = 'club'): Observable<AuthResponse> {
    const endpoint = API.AUTH.LOGIN;
    return this.httpClient.post<AuthResponse>(`${this.baseUrl}${endpoint}`, {
      username,
      password,
      federation_slug: this.federationSlug,
      user_object_type: platform === 'fed' ? 'FEDE' : 'CLUB'
    }, { withCredentials: true }).pipe(
      switchMap(loginResponse => {
        if (!loginResponse.success) return of(loginResponse);
        // Login succeeded — cookie is set. Now fetch permissions from /auth/me
        return this.httpClient.get<AuthResponse>(`${this.baseUrl}${API.AUTH.ME}`, { withCredentials: true }).pipe(
          tap(meResponse => {
            if (meResponse.success) {
              this.clearOtherPlatformCache(platform);
              this.setUser(meResponse.user);
              if (meResponse.club) this.setClub(meResponse.club);
              if (meResponse.federation) this.setFederation(meResponse.federation);
              this.currentUserSubject.next(meResponse.user);
            }
          }),
          // If /me fails, still use login response data (without permissions)
          catchError(() => {
            this.clearOtherPlatformCache(platform);
            this.setUser(loginResponse.user);
            if (loginResponse.club) this.setClub(loginResponse.club);
            if (loginResponse.federation) this.setFederation(loginResponse.federation);
            this.currentUserSubject.next(loginResponse.user);
            return of(loginResponse);
          })
        );
      })
    );
  }

  logout(): Observable<any> {
    const endpoint = API.AUTH.LOGOUT;
    // TASK-1.07: Cookie is sent automatically via withCredentials — no token in body needed
    // Backend uses req.user (from cookie auth) to identify the user
    return this.httpClient.post(`${this.baseUrl}${endpoint}`, {}, { withCredentials: true }).pipe(
      tap(() => this.clearAuth()),
      catchError((error: any) => {
        console.error('Logout error:', error);
        // Clear local state even if server logout fails
        this.clearAuth();
        return new Observable(observer => observer.complete());
      })
    );
  }

  // TASK-1.11: Transparent token refresh — cookie is sent automatically
  refreshToken(): Observable<any> {
    return this.httpClient.post(`${this.baseUrl}/auth/refresh`, {}, { withCredentials: true });
  }

  // Called by AuthInterceptor directly when refresh fails — avoids HTTP loop
  clearLocalAuth(): void {
    this.userDataSignal.set(null);
    this.clubDataSignal.set(null);
    this.federationDataSignal.set(null);
    this.currentUserSubject.next(null);
  }

  private clearAuth(): void {
    this.clearLocalAuth();
  }

  // TASK-1.09: Token is in HttpOnly cookie — JS cannot read it.
  // Club status is read from stored club_data instead of JWT payload.
  getClubStatusFromToken(): 'ACTI' | 'INAC' | 'DESA' | null {
    const club = this.getClub();
    if (!club) return null;
    const status = club.club_status || club.Statut_CLUB || null;
    if (status === 'ACTI' || status === 'INAC' || status === 'DESA') return status;
    return null;
  }

  getAccessLevel(): 'full' | 'readonly' | 'blocked' {
    if (!this.isAuthenticated()) return 'readonly';
    if (!this.isClub()) return 'full';

    const status = this.getClubStatusFromToken();
    if (status === 'ACTI') return 'full';
    if (status === 'INAC') return 'readonly';
    if (status === 'DESA') return 'blocked';
    return 'readonly';
  }

  setUser(user: User): void {
    this.userDataSignal.set(user);
  }

  getUser(): User | null {
    return this.userDataSignal();
  }

  // TASK-1.09: isAuthenticated checks user_data presence (not token)
  isAuthenticated(): boolean {
    return this.currentUserSubject.getValue() !== null;
  }

  isClub(): boolean {
    const user = this.getUser();
    return user?.user_object_type === 'CLUB';
  }

  isFederation(): boolean {
    const user = this.getUser();
    return user?.user_object_type === 'FEDE';
  }

  setClub(club: Club): void {
    this.clubDataSignal.set(club);
  }

  getClub(): Club | null {
    return this.clubDataSignal();
  }

  setFederation(federation: Federation): void {
    this.federationDataSignal.set(federation);
  }

  getFederation(): Federation | null {
    return this.federationDataSignal();
  }

  setSlugLogoUrl(url: string | null): void {
    this.slugLogoUrlSignal.set(url);
  }

  // DB logo (SLUG-LOGO file) first, else the logo bundled in assets for this slug
  getSlugLogoUrl(): string | null {
    const slug = this.federationSlug;
    return this.slugLogoUrlSignal() ?? (slug ? `assets/images/${slug.toLowerCase()}-logo.png` : null);
  }

  getClubId(): number | null {
    const club = this.getClub();
    return club ? club.club_id : null;
  }
}
